import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, ChevronDown, ArrowUpRight, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { MataBadge } from "@/components/MataMark";
import { useCart } from "@/context/CartContext";
import { BrainGlyph } from "@/components/BrainGlyph";
import { useOffFeed } from "@/context/OffFeedContext";


export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const { t } = useLang();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { registerBrainClick } = useOffFeed();

    useEffect(() => { setOpen(false); }, [pathname]);
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const handleBrainClick = (e) => {
        if (registerBrainClick(e.currentTarget)) { e.stopPropagation(); return; }
        if (pathname !== "/") navigate("/");
    };

    /* 6 primary items. Secondary pages live inside TRAILS and ABNORMAL. */
    const items = [
        { key: "trail", to: "/trail", label: t("nav.trail"), match: ["/trail", "/video", "/guide"], children: [
            { to: "/trail", label: t("nav.trailReport"), key: "trail-report" },
            { to: "/video", label: t("nav.video"), key: "video" },
            { to: "/guide", label: t("nav.guide"), key: "guide" },
        ]},
        { key: "ride-call", to: "/ride-call", label: t("nav.rides"), match: ["/ride-call"] },
        { key: "eventi", to: "/eventi", label: t("nav.eventi"), match: ["/eventi"] },
        { key: "community", to: "/community", label: t("nav.community"), match: ["/community"] },
        { key: "stay", to: "/stay", label: t("nav.stay"), match: ["/stay"] },
        { key: "abnormal", to: "/abnormal", label: t("nav.abnormal"), accent: true, match: ["/abnormal", "/storia", "/arcade"], children: [
            { to: "/abnormal#story", label: t("nav.story"), key: "story" },
            { to: "/abnormal#murph", label: t("nav.murph"), key: "murph" },
            { to: "/arcade", label: t("nav.arcade"), key: "arcade" },
        ]},
        { key: "shop", to: "/shop", label: t("nav.shop"), accent: true, match: ["/shop"] },
    ];
    const isActive = (it) => it.match.some((m) => pathname === m || pathname.startsWith(m + "/"));

    return (
        <header data-testid="main-navbar" className="sticky top-0 z-50 w-full bg-abnormal-ink/95 border-b border-white/10">
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between gap-4">
                <div className="group flex items-center gap-2 flex-shrink-0">
                    <button type="button" onClick={handleBrainClick} data-testid="brain-mark" aria-label="Abnormal mark"
                        className="inline-flex cursor-pointer select-none bg-transparent border-0 p-0 m-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-abnormal-acid">
                        <BrainGlyph size={22} strokeWidth={1.7} className="text-abnormal-acid transition-transform duration-500 group-hover:rotate-[10deg]" />
                    </button>
                    <Link to="/" data-testid="logo-home-link" className="flex items-baseline gap-2">
                        <span className="font-display text-xl min-[400px]:text-2xl sm:text-3xl leading-none text-abnormal-paper">ABNORMALWORLD</span>
                        <span className="hidden md:inline font-vt text-[9px] tracking-[0.3em] uppercase text-abnormal-paperDim/70">/ ARGENTARIO</span>
                    </Link>
                    <span className="hidden sm:inline w-px h-6 bg-white/15 mx-0.5 md:mx-2" aria-hidden="true" />
                    <MataBadge size={28} testid="nav-mata-badge" className="md:!w-9 md:!h-9" />
                </div>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-7 flex-1 justify-center" aria-label="Main">
                    {items.map((it) => (
                        <div key={it.key} className="relative group/nav">
                            <NavLink to={it.to} data-testid={`nav-${it.key}`}
                                className={`inline-flex items-center gap-1 uppercase text-[13px] font-bold tracking-[0.18em] transition-colors duration-200 py-5
                                    ${isActive(it) ? "text-abnormal-acid" : it.accent ? "text-abnormal-paper hover:text-abnormal-acid" : "text-abnormal-paperDim hover:text-abnormal-paper"}`}>
                                {it.accent && <span className="w-1.5 h-1.5 rounded-full bg-abnormal-acid mr-1" />}
                                {it.label}
                                {it.children && <ChevronDown size={12} className="opacity-60 group-hover/nav:rotate-180 transition-transform duration-200" />}
                            </NavLink>
                            {it.children && (
                                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-0 opacity-0 invisible translate-y-1 group-hover/nav:opacity-100 group-hover/nav:visible group-hover/nav:translate-y-0 transition-all duration-150">
                                    <div className="bg-abnormal-ink border border-white/15 min-w-[220px] py-2 shadow-2xl">
                                        {it.children.map((c, ci) => c.external ? (
                                            <a key={c.key} href={c.href} target="_blank" rel="noreferrer" data-testid={`nav-sub-${c.key}`}
                                                className="flex items-center justify-between gap-3 px-4 py-2.5 text-[12px] uppercase tracking-[0.18em] font-bold text-abnormal-acid hover:bg-white/5 border-t border-white/10 mt-1">
                                                {c.label} <ArrowUpRight size={12} />
                                            </a>
                                        ) : (
                                            <Link key={c.key} to={c.to} data-testid={`nav-sub-${c.key}`}
                                                className="flex items-center gap-3 px-4 py-2.5 text-[12px] uppercase tracking-[0.18em] font-bold text-abnormal-paperDim hover:text-abnormal-paper hover:bg-white/5">
                                                <span className="font-vt text-[9px] text-abnormal-acid">{String(ci + 1).padStart(2, "0")}</span>
                                                {c.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
                    <CartButton testid="nav-cart" />
                    <LanguageSwitcher />
                    {user ? (
                        <>
                            {user.role === "admin" && (
                                <>
                                    <Link to="/admin" data-testid="nav-admin" className="text-[11px] uppercase tracking-[0.2em] text-abnormal-acid hover:text-abnormal-paper font-bold">{t("nav.admin")}</Link>
                                    <Link to="/admin/settings" data-testid="nav-admin-settings" className="text-[11px] uppercase tracking-[0.2em] text-abnormal-paperDim hover:text-abnormal-paper font-bold">{t("nav.settings")}</Link>
                                </>
                            )}
                            <span data-testid="user-name-pill" className="text-[11px] uppercase tracking-[0.2em] text-abnormal-paperDim flex items-center gap-2"><User size={13} /> {user.name}</span>
                            <button data-testid="logout-btn" onClick={logout} className="text-[11px] uppercase tracking-[0.2em] text-abnormal-paperDim hover:text-abnormal-acid flex items-center gap-1"><LogOut size={13} /> {t("nav.logout")}</button>
                        </>
                    ) : (
                        <Link to="/login" data-testid="nav-login" className="text-[11px] uppercase tracking-[0.2em] text-abnormal-paperDim hover:text-abnormal-paper font-bold">{t("nav.login")}</Link>
                    )}
                </div>

                <div className="lg:hidden flex items-center gap-1.5">
                    <CartButton testid="nav-cart-mobile" />
                    <LanguageSwitcher />
                    <button data-testid="mobile-menu-toggle" aria-label="Menu" aria-expanded={open}
                        className="text-abnormal-paper w-11 h-11 -mr-2 inline-flex items-center justify-center" onClick={() => setOpen((v) => !v)}>
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {/* Mobile — full-screen, grouped, large touch targets */}
            {open && (
                <div data-testid="mobile-menu" className="lg:hidden fixed inset-x-0 top-16 bottom-0 bg-abnormal-ink overflow-y-auto topo-lines">
                    <div className="px-5 py-6 space-y-1">
                        {items.map((it, i) => (
                            <div key={it.key} className={`poster-in ${i > 0 ? "border-t border-white/10" : ""}`} style={{ animationDelay: `${i * 40}ms` }}>
                                <Link to={it.to} data-testid={`mobile-nav-${it.key}`}
                                    className={`flex items-center justify-between py-4 font-display uppercase text-4xl leading-none ${isActive(it) || it.accent ? "text-abnormal-acid" : "text-abnormal-paper"}`}>
                                    <span className="flex items-baseline gap-3">
                                        <span className="font-vt text-[10px] tracking-[0.3em] text-abnormal-paperDim/60">0{i + 1}</span>
                                        {it.label}
                                    </span>
                                    <ArrowUpRight size={18} className="text-abnormal-acid" />
                                </Link>
                                {it.children && (
                                    <div className="pb-4 pl-9 flex flex-wrap gap-2">
                                        {it.children.map((c) => c.external ? (
                                            <a key={c.key} href={c.href} target="_blank" rel="noreferrer" data-testid={`mobile-nav-sub-${c.key}`}
                                                className="px-3 py-2 border border-abnormal-acid/50 text-abnormal-acid text-[11px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1">
                                                {c.label} <ArrowUpRight size={11} />
                                            </a>
                                        ) : (
                                            <Link key={c.key} to={c.to} data-testid={`mobile-nav-sub-${c.key}`}
                                                className="px-3 py-2 border border-white/15 text-abnormal-paperDim text-[11px] uppercase tracking-[0.18em] font-bold">
                                                {c.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="px-5 py-5 border-t border-white/10 flex flex-wrap gap-4 items-center label-mono text-abnormal-paperDim">
                        {user ? (
                            <>
                                {user.role === "admin" && (
                                    <>
                                        <Link to="/admin" className="text-abnormal-acid font-bold">{t("nav.admin")}</Link>
                                        <Link to="/admin/settings" className="font-bold">{t("nav.settings")}</Link>
                                    </>
                                )}
                                <button data-testid="mobile-logout-btn" onClick={() => { logout(); setOpen(false); }} className="flex items-center gap-1 hover:text-abnormal-acid">
                                    <LogOut size={13} /> {t("nav.logout")} ({user.name})
                                </button>
                            </>
                        ) : (
                            <Link to="/login" className="font-bold text-abnormal-paper">{t("nav.login")}</Link>
                        )}
                        <span className="ml-auto text-abnormal-acid">42°26'N · 11°08'E</span>
                    </div>
                </div>
            )}
        </header>
    );
}


function CartButton({ testid }) {
    const { count, setOpen } = useCart();
    return (
        <button type="button" onClick={() => setOpen(true)} data-testid={testid} aria-label="Carrello"
            className="relative w-9 h-10 sm:w-10 inline-flex items-center justify-center text-abnormal-paper hover:text-abnormal-acid transition-colors">
            <ShoppingBag size={18} />
            {count > 0 && (
                <span data-testid={`${testid}-count`} className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-abnormal-acid text-black font-vt text-[10px] leading-[18px] text-center">{count}</span>
            )}
        </button>
    );
}
